import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from "react";

export default function createCustomContext<Store extends Record<string, any>>(
  initialState: Store
) {
  function CreateStore() {
    const store = useRef(initialState);

    const get = useCallback(() => store.current, []);

    const subscribers = useRef(new Set<() => void>());

    const set = useCallback((value: Partial<Store>) => {
      store.current = { ...store.current, ...value };
      subscribers.current.forEach((callback) => callback());
    }, []);

    const setOne = useCallback(
      <T extends keyof Store>(
        fieldName: T,
        selector: (value: Store[T]) => Store[T]
      ) => {
        store.current = {
          ...store.current,
          [fieldName]: selector(store.current[fieldName]),
        };
        subscribers.current.forEach((cb) => cb());
      },
      []
    );

    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    }, []);

    return {
      get,
      set,
      setOne,
      subscribe,
    };
  }

  type StoreReturnType = ReturnType<typeof CreateStore>;

  const StoreContext = createContext<StoreReturnType | null>(null);

  const withContext =
    <F extends JSX.IntrinsicAttributes>(Component: React.FC<F>) =>
    (props: F) => {
      return (
        <StoreContext.Provider value={CreateStore()}>
          <Component {...props} />
        </StoreContext.Provider>
      );
    };

  const useStore = (initProps?: Partial<Store>, updateOnChange: boolean = false) => {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error("Store not found");
    }

		
    const init = useRef<Partial<Store> | null>(null);
    if (updateOnChange && !Object.is(init.current, initProps)) {
      init.current = initProps;
      store.set(initProps);
    }
    if (!updateOnChange && initProps && !init.current) {
      init.current = initProps;
      store.set(initProps);
    }

    return { set: store.set, setOne: store.setOne, get: store.get };
  };

  function useStoreValue<SelectorOutput>(
    selector: (store: Store) => SelectorOutput
  ) {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error("Store not found");
    }

    const state = useSyncExternalStore(
      store.subscribe,
      () => selector(store.get()),
      () => selector(initialState)
    );

    return state;
  }

  return [useStoreValue, useStore, withContext] as [
    typeof useStoreValue,
    typeof useStore,
    typeof withContext,
  ];
}
